import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavHeader from '@/components/NavHeader';
import { useAuth } from '@pooflabs/web';
import { toast } from 'sonner';
import { PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeAllLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { setDonations } from '@/lib/collections/donations';
import { subscribeManyDonations } from '@/lib/collections/donations';
import type { DonationsResponse } from '@/lib/collections/donations';
import {
  setDonationFavorites,
  deleteDonationFavorites,
  subscribeManyDonationFavorites,
} from '@/lib/collections/donationFavorites';
import type { DonationFavoritesResponse } from '@/lib/collections/donationFavorites';
import { Time, Address } from '@/lib/db-client';
import { BlurFade } from '@/components/effects/BlurFade';
import { Particles } from '@/components/effects/Particles';
import {
  getTokenByMint,
  parseTokenAmount,
  formatTokenAmount,
  SOL,
  BONK,
  SKR,
  MEW,
  USDC,
  ANGELIC_SOL,
  GMT,
  DRAGON,
} from '@/utils/tokenConfig';
import { calculateDonationPoints, calculateLockPoints, formatPoints, truncateAddress, sumPendingAwarePoints } from '@/utils/pointsCalculator';
import {
  Lock,
  Heart,
  Users,
  Star,
  X,
  Plus,
  ChevronRight,
  Gift,
  Loader2,
  Sparkles,
  Coffee,
  Info,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { FEE_WALLET } from '@/lib/constants';
import { warmUsdPrices } from '@/utils/usdPricing';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOKENS = [SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON];

const ANGELIC_SOL_PUMP_URL = 'https://pump.fun/coin/3UAjbnd4w7MJstT3HjnyKBXZeiKUNU83cBL1ouDspump';

interface DonationTier {
  id: string;
  label: string;
  count: number | 'all';
  subtitle: string;
  multiplier: number;
  premium: boolean;
}

const TIERS: DonationTier[] = [
  { id: '10',  label: '10 Random', count: 10,    subtitle: 'Casual Giving',       multiplier: 1.5, premium: false },
  { id: '25',  label: '25 Random', count: 25,    subtitle: 'Generous',            multiplier: 2.0, premium: false },
  { id: '100', label: '100 Random',count: 100,   subtitle: 'Philanthropist',      multiplier: 3.0, premium: true  },
  { id: 'all', label: 'All Users', count: 'all', subtitle: 'Ultimate Selflessness',multiplier: 5.0, premium: true  },
];

const ALL_USERS_UNLOCK_THRESHOLD = 150;

const SLOT_TIERS = [
  { minPoints: 0,    slots: 0  },
  { minPoints: 100,  slots: 2  },
  { minPoints: 500,  slots: 4  },
  { minPoints: 1000, slots: 6  },
  { minPoints: 2500, slots: 8  },
  { minPoints: 5000, slots: 10 },
];

function getUnlockedSlots(points: number): number {
  let slots = 0;
  for (const tier of SLOT_TIERS) {
    if (points >= tier.minPoints) slots = tier.slots;
  }
  return slots;
}

function getNextSlotTier(points: number): { slots: number; minPoints: number } | null {
  for (const tier of SLOT_TIERS) {
    if (points < tier.minPoints) return tier;
  }
  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const TokenPill: React.FC<{
  mint: string;
  selected: boolean;
  onClick: () => void;
}> = ({ mint, selected, onClick }) => {
  const t = getTokenByMint(mint);
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-full text-sm font-bold tracking-wide transition-all duration-200"
      style={{
        background: selected ? t.brandColorBg : 'hsl(30 6% 7%)',
        color: selected ? t.brandColor : 'hsl(35 8% 45%)',
        border: selected ? `1px solid ${t.brandColorBorder}` : '1px solid hsl(35 12% 14%)',
        boxShadow: selected ? `0 0 12px ${t.brandColorBg}` : 'none',
        transform: selected ? 'scale(1.05)' : 'scale(1)',
      }}
    >
      {t.symbol}
    </button>
  );
};

const TierCard: React.FC<{
  tier: DonationTier;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
}> = ({ tier, selected, onClick, disabled, disabledReason }) => (
  <button
    onClick={disabled ? undefined : onClick}
    className="relative w-full rounded-xl p-4 text-left transition-all duration-200 group"
    style={{
      background: disabled
        ? 'hsl(30 4% 6%)'
        : selected
        ? 'linear-gradient(135deg, hsl(38 30% 8%) 0%, hsl(38 20% 11%) 100%)'
        : 'hsl(30 6% 7%)',
      border: disabled
        ? '1px solid hsl(35 10% 10%)'
        : selected
        ? '1px solid hsl(38 65% 57% / 0.5)'
        : '1px solid hsl(35 12% 14%)',
      boxShadow: selected && !disabled ? '0 0 20px hsl(38 65% 57% / 0.12)' : 'none',
      transform: selected && !disabled ? 'scale(1.02)' : 'scale(1)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {tier.premium && !disabled && (
      <span
        className="absolute top-2.5 right-2.5 text-xs px-1.5 py-0.5 rounded font-bold tracking-wide"
        style={{
          background: 'hsl(43 75% 68% / 0.15)',
          color: 'hsl(43 75% 68%)',
          border: '1px solid hsl(43 75% 68% / 0.3)',
          fontSize: '0.6rem',
        }}
      >
        PREMIUM
      </span>
    )}
    {disabled && (
      <span
        className="absolute top-2.5 right-2.5 text-xs px-1.5 py-0.5 rounded font-bold tracking-wide flex items-center gap-1"
        style={{
          background: 'hsl(35 10% 12%)',
          color: 'hsl(35 8% 40%)',
          border: '1px solid hsl(35 10% 16%)',
          fontSize: '0.6rem',
        }}
      >
        <Lock size={8} />
        LOCKED
      </span>
    )}
    <div className="flex items-start gap-3">
      <div
        className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
        style={{
          background: disabled ? 'hsl(30 5% 10%)' : selected ? 'hsl(38 65% 57%)' : 'hsl(30 5% 12%)',
          border: disabled
            ? '2px solid hsl(35 10% 16%)'
            : selected
            ? '2px solid hsl(38 65% 57%)'
            : '2px solid hsl(35 12% 20%)',
        }}
      >
        {selected && !disabled && (
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'hsl(30 8% 4%)' }} />
        )}
      </div>
      <div className="flex-1">
        <div
          className="text-base font-semibold leading-tight"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: disabled ? 'hsl(35 8% 38%)' : selected ? 'hsl(43 75% 68%)' : 'hsl(40 15% 80%)',
          }}
        >
          {tier.label}
        </div>
        <div className="text-xs mt-0.5" style={{ color: disabled ? 'hsl(35 8% 32%)' : 'hsl(35 8% 45%)' }}>
          {disabled && disabledReason ? disabledReason : tier.subtitle}
        </div>
        <div
          className="text-xs mt-1.5 font-medium"
          style={{ color: disabled ? 'hsl(35 8% 28%)' : selected ? 'hsl(38 65% 57%)' : 'hsl(35 8% 38%)' }}
        >
          {tier.multiplier}x points multiplier
        </div>
      </div>
    </div>
  </button>
);

// ─── Add Favorite Modal ───────────────────────────────────────────────────────

const AddFavoriteModal: React.FC<{
  open: boolean;
  onClose: () => void;
  candidates: string[];
  existingFavorites: string[];
  activeLockOwners: Set<string>;
  onAdd: (address: string) => void;
}> = ({ open, onClose, candidates, existingFavorites, activeLockOwners, onAdd }) => {
  const [search, setSearch] = useState('');
  const filtered = candidates
    .filter((a) => !existingFavorites.includes(a))
    .filter((a) => !search || a.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        style={{
          background: 'hsl(30 6% 7%)',
          border: '1px solid hsl(35 12% 14%)',
          color: 'hsl(40 15% 92%)',
          maxWidth: '480px',
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Add Selected User
          </DialogTitle>
          <DialogDescription style={{ color: 'hsl(35 8% 45%)' }}>
            Select a wallet address to add to your Selected Users list. Users without an active lock cannot receive donations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Search address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              background: 'hsl(30 5% 10%)',
              border: '1px solid hsl(35 12% 18%)',
              color: 'hsl(40 15% 88%)',
            }}
          />
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-sm text-center py-6" style={{ color: 'hsl(35 8% 45%)' }}>
                {candidates.length === 0 ? 'No lock owners found yet' : 'No matches'}
              </p>
            )}
            {filtered.slice(0, 30).map((addr) => {
              const hasActiveLock = activeLockOwners.has(addr);
              return (
                <button
                  key={addr}
                  onClick={() => { if (hasActiveLock) { onAdd(addr); onClose(); } }}
                  disabled={!hasActiveLock}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left"
                  style={{
                    background: hasActiveLock ? 'hsl(30 5% 10%)' : 'hsl(30 4% 8%)',
                    border: `1px solid ${hasActiveLock ? 'hsl(35 12% 14%)' : 'hsl(35 10% 11%)'}`,
                    color: hasActiveLock ? 'hsl(40 15% 80%)' : 'hsl(35 8% 38%)',
                    cursor: hasActiveLock ? 'pointer' : 'not-allowed',
                    opacity: hasActiveLock ? 1 : 0.6,
                  }}
                >
                  <span className="font-mono text-sm">{truncateAddress(addr)}</span>
                  {hasActiveLock ? (
                    <Plus size={14} style={{ color: 'hsl(38 65% 57%)' }} />
                  ) : (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                      style={{
                        background: 'hsl(35 10% 12%)',
                        color: 'hsl(35 8% 40%)',
                        border: '1px solid hsl(35 10% 16%)',
                        fontSize: '0.65rem',
                      }}
                    >
                      <Lock size={8} />
                      No active lock
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Donation Progress Modal ──────────────────────────────────────────────────

const DonationProgressModal: React.FC<{
  open: boolean;
  current: number;
  total: number;
  failed: number;
  skipped: number;
  done: boolean;
  totalDonated: string;
  pointsEarned: number | null;
  onClose: () => void;
}> = ({ open, current, total, failed, skipped, done, totalDonated, pointsEarned, onClose }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && done) onClose(); }}>
      <DialogContent
        aria-describedby="donation-progress-desc"
        style={{
          background: 'hsl(30 6% 7%)',
          border: '1px solid hsl(35 12% 14%)',
          color: 'hsl(40 15% 92%)',
          maxWidth: '440px',
        }}
      >
        <span id="donation-progress-desc" className="sr-only">
          {done ? 'Donation complete' : 'Donation in progress'}
        </span>
        <div className="text-center py-4">
          {done ? (
            <>
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{
                  background: 'hsl(38 65% 57% / 0.15)',
                  border: '1px solid hsl(38 65% 57% / 0.4)',
                }}
              >
                <Heart size={28} style={{ color: 'hsl(38 65% 57%)' }} />
              </div>
              <h3
                className="text-2xl font-semibold mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(43 75% 68%)' }}
              >
                Donation Complete
              </h3>
              {failed > 0 && (
                <p className="text-sm mb-2" style={{ color: 'hsl(0 65% 60%)' }}>
                  {failed} transaction{failed > 1 ? 's' : ''} failed — {current - failed} succeeded
                </p>
              )}
              {skipped > 0 && (
                <div
                  className="flex items-start gap-2 px-3 py-2 rounded-lg mb-3 text-left mx-auto"
                  style={{
                    background: 'hsl(38 30% 8%)',
                    border: '1px solid hsl(38 40% 20% / 0.5)',
                    maxWidth: '320px',
                  }}
                >
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" style={{ color: 'hsl(38 65% 57%)' }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'hsl(38 20% 60%)' }}>
                    {skipped} recipient{skipped > 1 ? 's were' : ' was'} skipped — no active lock found. Only users who are actively locking tokens can receive donations.
                  </p>
                </div>
              )}
              <div
                className="inline-flex items-center gap-6 px-6 py-3 rounded-xl mt-2 mb-6"
                style={{
                  background: 'hsl(38 65% 57% / 0.08)',
                  border: '1px solid hsl(38 65% 57% / 0.2)',
                }}
              >
                <div className="text-left">
                  <div className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'hsl(35 8% 45%)' }}>Donated</div>
                  <div className="font-bold" style={{ color: 'hsl(43 75% 68%)', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem' }}>{totalDonated}</div>
                </div>
                <div className="w-px h-10 self-center" style={{ background: 'hsl(38 65% 57% / 0.2)' }} />
                <div className="text-left">
                  <div className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'hsl(35 8% 45%)' }}>Points Earned</div>
                  <div className="font-bold" style={{ color: 'hsl(38 65% 57%)', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem' }}>+{formatPoints(pointsEarned)}</div>
                </div>
              </div>
              <Button
                onClick={onClose}
                style={{
                  background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                  color: 'hsl(30 8% 4%)',
                  border: 'none',
                }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <div className="mb-4">
                <Loader2 size={32} className="animate-spin mx-auto" style={{ color: 'hsl(38 65% 57%)' }} />
              </div>
              <h3
                className="text-xl font-semibold mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Sending Donations…
              </h3>
              <p className="text-sm mb-5" style={{ color: 'hsl(35 8% 52%)' }}>
                Donated to {current} of {total} users
              </p>
              <div className="mb-2">
                <Progress value={pct} className="h-2" />
              </div>
              <p className="text-xs" style={{ color: 'hsl(35 8% 40%)' }}>{pct}% complete — each donation is an on-chain transaction</p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Donation History ─────────────────────────────────────────────────────────

interface DonationBatch {
  batchId: string;
  tokenMint: string;
  totalAmount: number;
  recipientCount: number;
  ts: number;
  pointsEarned: number | null; // null = USD price for tokenMint currently unavailable
}

const DonationHistory: React.FC<{ donations: DonationsResponse[]; walletAddress: string }> = ({
  donations,
  walletAddress,
}) => {
  const batches = useMemo((): DonationBatch[] => {
    const map = new Map<string, DonationBatch>();
    const myDonations = donations.filter((d) => d.donor === walletAddress);
    for (const d of myDonations) {
      const key = d.batchId;
      if (!map.has(key)) {
        map.set(key, {
          batchId: d.batchId,
          tokenMint: d.tokenMint,
          totalAmount: 0,
          recipientCount: 0,
          ts: d.ts,
          pointsEarned: 0,
        });
      }
      const batch = map.get(key)!;
      batch.totalAmount += d.amount;
      batch.recipientCount += 1;
      if (d.ts < batch.ts) batch.ts = d.ts;
    }
    // compute points
    for (const batch of map.values()) {
      const decimals = getTokenByMint(batch.tokenMint).decimals;
      const divisor = Math.pow(10, decimals);
      const totalHuman = batch.totalAmount / divisor;
      batch.pointsEarned = calculateDonationPoints(totalHuman, batch.recipientCount, 0, batch.tokenMint);
    }
    return Array.from(map.values()).sort((a, b) => b.ts - a.ts);
  }, [donations, walletAddress]);

  if (batches.length === 0) return null;

  return (
    <BlurFade delay={0.2}>
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'hsl(30 6% 7%)',
          border: '1px solid hsl(35 12% 14%)',
        }}
      >
        <div
          className="flex items-center gap-2 px-5 py-4"
          style={{ borderBottom: '1px solid hsl(35 12% 14%)' }}
        >
          <Heart size={15} style={{ color: 'hsl(38 65% 57%)' }} />
          <h3
            className="font-semibold"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}
          >
            Your Donation History
          </h3>
        </div>
        {/* Column headers */}
        <div
          className="grid px-5 py-2 text-xs font-medium uppercase tracking-wider"
          style={{
            gridTemplateColumns: '1fr auto auto auto',
            color: 'hsl(35 8% 38%)',
            gap: '1rem',
            borderBottom: '1px solid hsl(35 12% 11%)',
          }}
        >
          <span>Date</span>
          <span className="text-right">Token</span>
          <span className="text-right">Recipients</span>
          <span className="text-right">Points</span>
        </div>
        <div>
          {batches.map((batch) => {
            const token = getTokenByMint(batch.tokenMint);
            const date = new Date(batch.ts * 1000).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            });
            return (
              <div
                key={batch.batchId}
                className="grid px-5 py-3.5 transition-colors"
                style={{
                  gridTemplateColumns: '1fr auto auto auto',
                  gap: '1rem',
                  borderBottom: '1px solid hsl(35 12% 10%)',
                }}
              >
                <div>
                  <div className="text-sm" style={{ color: 'hsl(40 15% 75%)' }}>{date}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'hsl(35 8% 40%)' }}>
                    {formatTokenAmount(batch.totalAmount, batch.tokenMint)} total
                  </div>
                </div>
                <div className="text-right flex items-center justify-end">
                  <span
                    className="text-xs px-2 py-0.5 rounded font-bold"
                    style={{
                      background: token.brandColorBg,
                      color: token.brandColor,
                      border: `1px solid ${token.brandColorBorder}`,
                    }}
                  >
                    {token.symbol}
                  </span>
                </div>
                <div
                  className="text-sm text-right flex items-center justify-end gap-1"
                  style={{ color: 'hsl(35 8% 55%)' }}
                >
                  <Users size={12} />
                  {batch.recipientCount}
                </div>
                <div
                  className="text-sm font-bold text-right flex items-center justify-end gap-1"
                  style={{ color: 'hsl(43 75% 68%)' }}
                >
                  <Star size={12} />
                  +{formatPoints(batch.pointsEarned)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BlurFade>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const DonatePage: React.FC = () => {
  const { user, login, loading: authLoading } = useAuth();
  const walletAddress = user?.address;

  // State
  const [selectedToken, setSelectedToken] = useState(SOL);
  const [amount, setAmount] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('25');
  const [includeFavorites, setIncludeFavorites] = useState(true);
  const [addFavoriteOpen, setAddFavoriteOpen] = useState(false);

  // Creator donation state
  const [creatorToken, setCreatorToken] = useState(SOL);
  const [creatorAmount, setCreatorAmount] = useState('');
  const [creatorDonating, setCreatorDonating] = useState(false);
  const [creatorDoneOpen, setCreatorDoneOpen] = useState(false);
  const [creatorPointsEarned, setCreatorPointsEarned] = useState<number | null>(0);

  // Donation progress
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressFailed, setProgressFailed] = useState(0);
  const [progressSkipped, setProgressSkipped] = useState(0);
  const [progressDone, setProgressDone] = useState(false);
  const [progressTotalDonated, setProgressTotalDonated] = useState('');
  const [progressPoints, setProgressPoints] = useState<number | null>(0);

  // Data
  const { data: allLocks } = useRealtimeData<LocksResponse[]>(subscribeAllLocks, true);

  const favFilter = walletAddress ? `where owner = '${walletAddress}'` : '';
  const { data: myFavorites } = useRealtimeData<DonationFavoritesResponse[]>(
    subscribeManyDonationFavorites,
    !!walletAddress,
    favFilter,
  );

  const donorFilter = walletAddress ? `where donor = '${walletAddress}'` : '';
  const { data: myDonations } = useRealtimeData<DonationsResponse[]>(
    subscribeManyDonations,
    !!walletAddress,
    donorFilter,
  );

  // Warm up the USD price cache for all supported tokens on mount, staggered so we
  // don't fire a burst of simultaneous Jupiter quotes. USDC is resolved statically
  // (it's the quote denominator) so it's excluded from the warm list.
  useEffect(() => {
    const cancel = warmUsdPrices([SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON]);
    return cancel;
  }, []);

  // Derived data

  // Map: owner address → first active lock ID for that owner (used to satisfy recipientLockId requirement)
  const activeLockMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const lock of allLocks ?? []) {
      if (lock.status === 'active' && lock.owner && !map.has(lock.owner)) {
        map.set(lock.owner, lock.id);
      }
    }
    return map;
  }, [allLocks]);

  // Set of all addresses that have at least one active lock (eligible for donations)
  const activeLockOwners = useMemo(() => new Set(activeLockMap.keys()), [activeLockMap]);

  const allLockOwners = useMemo(() => {
    const owners = new Set<string>();
    for (const lock of allLocks ?? []) {
      // Exclude the donor's own address and the FEE_WALLET protocol address —
      // FEE_WALLET should never appear in the random community donation pool.
      if (lock.owner && lock.owner !== walletAddress && lock.owner !== FEE_WALLET) {
        owners.add(lock.owner);
      }
    }
    return Array.from(owners);
  }, [allLocks, walletAddress]);

  // Total unique users across the platform (including self), used for the 150-user gate
  const totalUniqueUsers = useMemo(() => {
    const owners = new Set<string>();
    for (const lock of allLocks ?? []) {
      if (lock.owner) owners.add(lock.owner);
    }
    return owners.size;
  }, [allLocks]);

  const allUsersUnlocked = totalUniqueUsers >= ALL_USERS_UNLOCK_THRESHOLD;

  const favorites = myFavorites ?? [];
  const favoriteAddresses = favorites.map((f) => f.favoriteAddress);

  // Points earned so far (lock points only — donation points come from donation batches).
  // calculateLockPoints returns null when a token's USD price is currently unavailable —
  // those locks are excluded from the total (never folded in as 0) and counted as pending.
  const myLocks = (allLocks ?? []).filter((l) => l.owner === walletAddress);
  const { total: lockPoints, pendingCount: pendingLockPoints } = sumPendingAwarePoints(
    myLocks.map((l) => calculateLockPoints(l)),
  );

  const donationHistory = useMemo((): DonationBatch[] => {
    const map = new Map<string, DonationBatch>();
    for (const d of myDonations ?? []) {
      if (d.donor !== walletAddress) continue;
      const key = d.batchId;
      if (!map.has(key)) {
        map.set(key, { batchId: d.batchId, tokenMint: d.tokenMint, totalAmount: 0, recipientCount: 0, ts: d.ts, pointsEarned: 0 });
      }
      const batch = map.get(key)!;
      batch.totalAmount += d.amount;
      batch.recipientCount += 1;
      if (d.ts < batch.ts) batch.ts = d.ts;
    }
    for (const batch of map.values()) {
      const decimals = getTokenByMint(batch.tokenMint).decimals;
      batch.pointsEarned = calculateDonationPoints(batch.totalAmount / Math.pow(10, decimals), batch.recipientCount, 0, batch.tokenMint);
    }
    return Array.from(map.values());
  }, [myDonations, walletAddress]);

  const { total: totalDonationPoints, pendingCount: pendingDonationPoints } = sumPendingAwarePoints(
    donationHistory.map((b) => b.pointsEarned),
  );
  const totalSelflessPoints = lockPoints + totalDonationPoints;
  const pendingSelflessPoints = pendingLockPoints + pendingDonationPoints;

  const unlockedSlots = getUnlockedSlots(totalSelflessPoints);
  const nextSlotTier = getNextSlotTier(totalSelflessPoints);

  // Tier selection
  const activeTier = TIERS.find((t) => t.id === selectedTier) ?? TIERS[1];

  // Eligible lock owners = users (not self) who have at least one active lock
  const eligibleLockOwners = useMemo(
    () => allLockOwners.filter((a) => activeLockOwners.has(a)),
    [allLockOwners, activeLockOwners],
  );

  // If the currently selected tier becomes unavailable (not enough active accounts),
  // fall back to the highest available count tier automatically.
  useEffect(() => {
    const activeCount = eligibleLockOwners.length;
    const isTierUnavailable = (tier: DonationTier) => {
      if (tier.id === 'all') return !allUsersUnlocked;
      if (typeof tier.count === 'number') return activeCount < tier.count;
      return false;
    };
    if (isTierUnavailable(activeTier)) {
      // Find the highest count tier that IS available (descending)
      const fallback = [...TIERS]
        .reverse()
        .find((t) => !isTierUnavailable(t));
      if (fallback) setSelectedTier(fallback.id);
    }
  }, [eligibleLockOwners.length, allUsersUnlocked, activeTier]);

  // Recipient calculation
  const baseRecipientCount = useMemo(() => {
    if (activeTier.count === 'all') return eligibleLockOwners.length;
    return Math.min(activeTier.count as number, eligibleLockOwners.length);
  }, [activeTier, eligibleLockOwners]);

  const includedFavoriteAddresses = useMemo(() => {
    if (!includeFavorites) return [];
    return favoriteAddresses.filter((fa) => fa !== walletAddress);
  }, [includeFavorites, favoriteAddresses, walletAddress]);

  // Favorites that are actually eligible (have active lock)
  const eligibleFavoriteAddresses = useMemo(
    () => includedFavoriteAddresses.filter((a) => activeLockOwners.has(a)),
    [includedFavoriteAddresses, activeLockOwners],
  );

  const totalRecipientCount = useMemo(() => {
    // Base random pool from eligible users, minus any already in eligible favorites
    const randomPool = eligibleLockOwners.filter((a) => !eligibleFavoriteAddresses.includes(a));
    const randomCount = Math.min(baseRecipientCount, randomPool.length);
    const favCount = eligibleFavoriteAddresses.length;
    return randomCount + favCount;
  }, [baseRecipientCount, eligibleLockOwners, eligibleFavoriteAddresses]);

  const amountNum = parseFloat(amount) || 0;
  const amountRaw = parseTokenAmount(amountNum, selectedToken);
  const perPersonRaw = totalRecipientCount > 0 ? Math.floor(amountRaw / totalRecipientCount) : 0;
  const perPersonHuman = amountNum > 0 && totalRecipientCount > 0 ? amountNum / totalRecipientCount : 0;

  const estimatedPoints = useMemo(
    () => calculateDonationPoints(amountNum, totalRecipientCount, eligibleFavoriteAddresses.length, selectedToken),
    [amountNum, totalRecipientCount, eligibleFavoriteAddresses.length, selectedToken],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleAddFavorite = useCallback(async (address: string) => {
    if (!walletAddress) return;
    if (favorites.length >= unlockedSlots) {
      toast.error('All slots are in use');
      return;
    }
    const favoriteId = `${walletAddress}_${address}`;
    const success = await setDonationFavorites(favoriteId, {
      owner: Address.publicKey(walletAddress),
      favoriteAddress: Address.publicKey(address),
      ts: Time.Now,
    });
    if (success) {
      toast.success('Added to Selected Users');
    } else {
      toast.error('Failed to add user');
    }
  }, [walletAddress, favorites.length, unlockedSlots]);

  const handleRemoveFavorite = useCallback(async (fav: DonationFavoritesResponse) => {
    const success = await deleteDonationFavorites(fav.id);
    if (success) {
      toast.success('Removed from Selected Users');
    } else {
      toast.error('Failed to remove user');
    }
  }, []);

  const handleDonate = useCallback(async () => {
    if (!walletAddress) { login(); return; }
    if (amountNum <= 0) { toast.error('Enter a donation amount'); return; }
    if (totalRecipientCount === 0) { toast.error('No eligible recipients — all must have an active lock'); return; }
    if (perPersonRaw <= 0) { toast.error('Amount too small to split between recipients'); return; }

    // Build recipient list from eligible users only
    const randomPool = eligibleLockOwners.filter((a) => !eligibleFavoriteAddresses.includes(a));
    const shuffled = [...randomPool].sort(() => Math.random() - 0.5);
    const randomPick = shuffled.slice(0, baseRecipientCount);
    const recipients = [...new Set([...eligibleFavoriteAddresses, ...randomPick])];

    // Count ineligible favorites that got skipped
    const ineligibleFavCount = includedFavoriteAddresses.length - eligibleFavoriteAddresses.length;

    const batchId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const token = getTokenByMint(selectedToken);

    setProgressCurrent(0);
    setProgressTotal(recipients.length);
    setProgressFailed(0);
    setProgressSkipped(ineligibleFavCount);
    setProgressDone(false);
    setProgressOpen(true);

    let failedCount = 0;
    let successCount = 0;

    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      const recipientLockId = activeLockMap.get(recipient);
      if (!recipientLockId) {
        // Defensive: skip if no active lock (shouldn't happen since we filtered, but be safe)
        setProgressSkipped((prev) => prev + 1);
        setProgressCurrent(i + 1);
        continue;
      }
      const donationId = `${batchId}_${i}`;
      const success = await setDonations(donationId, {
        donor: Address.publicKey(walletAddress),
        recipient: Address.publicKey(recipient),
        tokenMint: selectedToken,
        amount: perPersonRaw,
        batchId,
        ts: Time.Now,
        recipientLockId,
      });

      if (success) {
        successCount++;
      } else {
        failedCount++;
      }
      setProgressCurrent(i + 1);
      setProgressFailed(failedCount);
    }

    const totalHuman = (perPersonRaw * successCount) / Math.pow(10, token.decimals);
    const pts = calculateDonationPoints(totalHuman, successCount, eligibleFavoriteAddresses.length, selectedToken);
    setProgressTotalDonated(formatTokenAmount(perPersonRaw * successCount, selectedToken));
    setProgressPoints(pts);
    setProgressDone(true);

    if (failedCount === 0 && ineligibleFavCount === 0) {
      toast.success(`Donated to ${successCount} users!`);
    } else if (successCount > 0) {
      const parts: string[] = [`Donated to ${successCount} users`];
      if (failedCount > 0) parts.push(`${failedCount} failed`);
      if (ineligibleFavCount > 0) parts.push(`${ineligibleFavCount} skipped (no active lock)`);
      toast.success(parts.join(' — '));
    } else {
      toast.error('All donations failed — please try again');
    }
  }, [
    walletAddress, login, amountNum, totalRecipientCount, perPersonRaw, eligibleLockOwners,
    eligibleFavoriteAddresses, includedFavoriteAddresses, baseRecipientCount, selectedToken,
    activeLockMap,
  ]);

  const handleCreatorDonate = useCallback(async () => {
    if (!walletAddress) { login(); return; }
    const creatorAmountNum = parseFloat(creatorAmount) || 0;
    if (creatorAmountNum <= 0) { toast.error('Enter a donation amount'); return; }
    const rawAmt = parseTokenAmount(creatorAmountNum, creatorToken);
    if (rawAmt <= 0) { toast.error('Amount too small'); return; }

    const recipientLockId = activeLockMap.get(FEE_WALLET);
    if (!recipientLockId) {
      toast.error('Creator support is temporarily unavailable — creator has no active lock');
      return;
    }

    setCreatorDonating(true);
    const batchId = `creator_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const donationId = `${batchId}_0`;
    const success = await setDonations(donationId, {
      donor: Address.publicKey(walletAddress),
      recipient: Address.publicKey(FEE_WALLET),
      tokenMint: creatorToken,
      amount: rawAmt,
      batchId,
      ts: Time.Now,
      recipientLockId,
    });
    setCreatorDonating(false);

    if (success) {
      // Points: single recipient = 1.5x tier, no selected users
      const pts = calculateDonationPoints(creatorAmountNum, 1, 0, creatorToken);
      setCreatorPointsEarned(pts);
      setCreatorDoneOpen(true);
      setCreatorAmount('');
      toast.success('Thank you for supporting the creator!');
    } else {
      toast.error('Donation failed — please try again');
    }
  }, [walletAddress, login, creatorAmount, creatorToken, activeLockMap]);

  // ─── Auth gate ───────────────────────────────────────────────────────────────

  if (!authLoading && !user) {
    return (
      <PageLayout fullBleed footer={false}>
        <NavHeader />
        <div className="relative min-h-[80vh] flex items-center justify-center pt-16">
          <Particles quantity={20} color="hsl(38 65% 57%)" />
          <div className="relative z-10 text-center max-w-md mx-auto px-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'hsl(38 65% 57% / 0.12)', border: '1px solid hsl(38 65% 57% / 0.3)' }}>
              <Heart size={24} style={{ color: 'hsl(38 65% 57%)' }} />
            </div>
            <h2 className="text-3xl font-semibold mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Connect to Donate</h2>
            <p className="text-sm mb-8 leading-relaxed" style={{ color: 'hsl(35 8% 52%)' }}>Connect your Solana wallet to spread the wealth and earn Selfless Points.</p>
            <Button size="lg" onClick={() => login()} className="px-10 py-5 font-medium" style={{ background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))', color: 'hsl(30 8% 4%)', border: 'none' }}>Connect Wallet</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout fullBleed footer={false}>
      <NavHeader />
      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 0%, hsl(38 65% 57% / 0.09), transparent)' }} />
        <Particles quantity={15} color="hsl(38 65% 57%)" />
        <div className="relative z-10 container text-center">
          <BlurFade delay={0.05}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-5 tracking-widest uppercase" style={{ background: 'hsl(38 65% 57% / 0.1)', border: '1px solid hsl(38 65% 57% / 0.25)', color: 'hsl(43 75% 68%)' }}>
              <Heart size={10} />
              Donation Protocol
            </div>
          </BlurFade>
          <BlurFade delay={0.12}>
            <h1 className="mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 600, background: 'linear-gradient(135deg, hsl(40 15% 92%) 0%, hsl(43 75% 68%) 50%, hsl(38 65% 57%) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1.1 }}>
              Spread the Wealth
            </h1>
          </BlurFade>
          <BlurFade delay={0.2}>
            <p className="text-base max-w-md mx-auto leading-relaxed" style={{ color: 'hsl(35 8% 52%)' }}>
              Donate tokens directly to fellow SelfWorth users — and earn Selfless Points for every generous act.
            </p>
          </BlurFade>
        </div>
      </section>

      <div className="container pb-20">
        {/* ─── AngelicSol callout ─────────────────────────────────────────────── */}
        <BlurFade delay={0.08}>
          <div
            className="max-w-5xl mx-auto mb-6 px-4 py-3 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            style={{
              background: 'linear-gradient(135deg, hsl(280 50% 8%) 0%, hsl(280 40% 11%) 100%)',
              border: '1px solid hsl(280 70% 60% / 0.35)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: 'hsl(280 70% 60% / 0.15)',
                  border: '1px solid hsl(280 70% 60% / 0.35)',
                  color: 'hsl(280 80% 72%)',
                }}
              >
                <Sparkles size={15} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'hsl(280 80% 82%)' }}>
                  AngelicSol (ANGEL) now supported
                </p>
                <p className="text-xs" style={{ color: 'hsl(280 30% 60%)' }}>
                  ANGEL is a Solana meme coin on pump.fun designed with donations in mind — tip it freely or buy it directly on pump.fun
                </p>
              </div>
            </div>
            <a
              href={ANGELIC_SOL_PUMP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-opacity hover:opacity-80 whitespace-nowrap shrink-0"
              style={{
                background: 'hsl(280 70% 60% / 0.2)',
                border: '1px solid hsl(280 70% 60% / 0.5)',
                color: 'hsl(280 80% 80%)',
              }}
            >
              <ExternalLink size={12} />
              Buy on pump.fun
            </a>
          </div>
        </BlurFade>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* ─── Left: Make a Donation ─────────────────────────────────────── */}
          <BlurFade delay={0.1}>
            <div className="rounded-xl overflow-hidden" style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}>
              {/* Card Header */}
              <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid hsl(35 12% 14%)' }}>
                <Gift size={15} style={{ color: 'hsl(38 65% 57%)' }} />
                <h2 className="font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem' }}>Make a Donation</h2>
              </div>

              <div className="p-5 space-y-6">
                {/* Active lock requirement notice */}
                <div
                  className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg"
                  style={{
                    background: 'hsl(210 40% 8%)',
                    border: '1px solid hsl(210 40% 20% / 0.5)',
                  }}
                >
                  <Info size={13} className="mt-0.5 shrink-0" style={{ color: 'hsl(210 70% 60%)' }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'hsl(210 30% 60%)' }}>
                    Donations can only be sent to users with an active lock. Ineligible users are automatically excluded from the recipient pool.
                  </p>
                </div>

                {/* Token selector */}
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: 'hsl(35 8% 45%)' }}>Token</label>
                  <div className="flex flex-wrap gap-2">
                    {TOKENS.map((mint) => (
                      <TokenPill
                        key={mint}
                        mint={mint}
                        selected={selectedToken === mint}
                        onClick={() => setSelectedToken(mint)}
                      />
                    ))}
                  </div>
                  {/* AngelicSol pump.fun link — shown when ANGEL is selected */}
                  {selectedToken === ANGELIC_SOL && (
                    <a
                      href={ANGELIC_SOL_PUMP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium transition-opacity hover:opacity-80"
                      style={{ color: 'hsl(280 80% 72%)' }}
                    >
                      <ExternalLink size={11} />
                      Buy AngelicSol on pump.fun
                    </a>
                  )}
                </div>

                {/* Amount input */}
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: 'hsl(35 8% 45%)' }}>
                    Total Amount ({getTokenByMint(selectedToken).symbol})
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    placeholder={`e.g. ${(selectedToken === BONK || selectedToken === MEW || selectedToken === GMT) ? '10000' : (selectedToken === USDC) ? '10' : '1'}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    style={{
                      background: 'hsl(30 5% 10%)',
                      border: '1px solid hsl(35 12% 18%)',
                      color: 'hsl(40 15% 88%)',
                      fontSize: '1rem',
                    }}
                  />
                </div>

                {/* Tier selector */}
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: 'hsl(35 8% 45%)' }}>
                    Donation Tier
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIERS.map((tier) => {
                      const isAllTier = tier.id === 'all';
                      const isCountTier = typeof tier.count === 'number';
                      const activeCount = eligibleLockOwners.length;
                      const isTierDisabled =
                        (isAllTier && !allUsersUnlocked) ||
                        (isCountTier && activeCount < (tier.count as number));
                      const disabledReason = isAllTier && !allUsersUnlocked
                        ? `Unlocks at ${ALL_USERS_UNLOCK_THRESHOLD} users (${totalUniqueUsers} now)`
                        : isCountTier && activeCount < (tier.count as number)
                        ? `Only ${activeCount} active account${activeCount === 1 ? '' : 's'} — need ${tier.count}`
                        : undefined;
                      return (
                        <TierCard
                          key={tier.id}
                          tier={tier}
                          selected={selectedTier === tier.id}
                          onClick={() => setSelectedTier(tier.id)}
                          disabled={isTierDisabled}
                          disabledReason={disabledReason}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Favorites toggle */}
                {favoriteAddresses.length > 0 && (
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer"
                    style={{ background: 'hsl(30 5% 10%)', border: '1px solid hsl(35 12% 16%)' }}
                    onClick={() => setIncludeFavorites((v) => !v)}
                  >
                    <div className="flex items-center gap-2">
                      <Star size={14} style={{ color: 'hsl(43 75% 68%)' }} />
                      <span className="text-sm" style={{ color: 'hsl(40 15% 80%)' }}>Include Selected Users</span>
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                        style={{ background: 'hsl(43 75% 68% / 0.15)', color: 'hsl(43 75% 68%)', border: '1px solid hsl(43 75% 68% / 0.3)' }}
                      >
                        {favoriteAddresses.length}
                      </span>
                    </div>
                    <div
                      className="w-10 h-5 rounded-full transition-all duration-200 relative"
                      style={{ background: includeFavorites ? 'hsl(38 65% 57%)' : 'hsl(30 5% 16%)' }}
                    >
                      <div
                        className="absolute top-0.5 w-4 h-4 rounded-full transition-all duration-200"
                        style={{ background: 'hsl(30 8% 4%)', left: includeFavorites ? '1.25rem' : '0.125rem' }}
                      />
                    </div>
                  </div>
                )}

                {/* Preview summary */}
                {amountNum > 0 && totalRecipientCount > 0 && (
                  <div
                    className="rounded-lg p-4 space-y-2"
                    style={{ background: 'hsl(38 65% 57% / 0.06)', border: '1px solid hsl(38 65% 57% / 0.18)' }}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: 'hsl(35 8% 52%)' }}>Recipients</span>
                      <span className="font-medium" style={{ color: 'hsl(40 15% 80%)' }}>{totalRecipientCount.toLocaleString()} users</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: 'hsl(35 8% 52%)' }}>Each receives</span>
                      <span className="font-medium" style={{ color: 'hsl(40 15% 80%)' }}>
                        {perPersonHuman < 0.000001 ? '< 0.000001' : perPersonHuman.toFixed(Math.min(6, getTokenByMint(selectedToken).decimals))} {getTokenByMint(selectedToken).symbol}
                      </span>
                    </div>
                    <div
                      className="flex items-center justify-between text-sm pt-2"
                      style={{ borderTop: '1px solid hsl(38 65% 57% / 0.15)' }}
                    >
                      <span style={{ color: 'hsl(38 65% 57%)' }}>Selfless Points</span>
                      <span className="font-bold" style={{ color: 'hsl(43 75% 68%)', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>
                        +{formatPoints(estimatedPoints)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs" style={{ color: 'hsl(35 8% 38%)' }}>
                      <span>{activeTier.multiplier}x tier multiplier</span>
                      {eligibleFavoriteAddresses.length > 0 && (
                        <span>+{eligibleFavoriteAddresses.length * 30}% favorites bonus</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Ineligible favorites warning */}
                {includeFavorites && includedFavoriteAddresses.length > 0 && eligibleFavoriteAddresses.length < includedFavoriteAddresses.length && (
                  <div
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
                    style={{
                      background: 'hsl(38 25% 7%)',
                      border: '1px solid hsl(38 40% 18% / 0.6)',
                    }}
                  >
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" style={{ color: 'hsl(38 65% 55%)' }} />
                    <p className="text-xs leading-relaxed" style={{ color: 'hsl(38 20% 55%)' }}>
                      {includedFavoriteAddresses.length - eligibleFavoriteAddresses.length} of your selected user{includedFavoriteAddresses.length - eligibleFavoriteAddresses.length > 1 ? 's' : ''} will be skipped — no active lock.
                    </p>
                  </div>
                )}

                {/* Donate button */}
                {/* If 'all' tier is selected but locked, show a disabled message instead */}
                {selectedTier === 'all' && !allUsersUnlocked ? (
                  <div
                    className="w-full py-4 rounded-lg text-center"
                    style={{
                      background: 'hsl(30 5% 8%)',
                      border: '1px dashed hsl(35 10% 16%)',
                    }}
                  >
                    <p className="text-sm font-medium mb-0.5" style={{ color: 'hsl(35 8% 42%)' }}>
                      <Lock size={13} className="inline mr-1.5 mb-0.5" />
                      Donate to All Users unavailable
                    </p>
                    <p className="text-xs" style={{ color: 'hsl(35 8% 32%)' }}>
                      Available when {ALL_USERS_UNLOCK_THRESHOLD}+ users join ({totalUniqueUsers}/{ALL_USERS_UNLOCK_THRESHOLD} now)
                    </p>
                  </div>
                ) : totalRecipientCount === 0 && amountNum > 0 ? (
                  <div
                    className="w-full py-4 rounded-lg text-center"
                    style={{
                      background: 'hsl(30 5% 8%)',
                      border: '1px dashed hsl(35 10% 16%)',
                    }}
                  >
                    <p className="text-sm font-medium mb-0.5" style={{ color: 'hsl(35 8% 42%)' }}>
                      <Lock size={13} className="inline mr-1.5 mb-0.5" />
                      No eligible recipients
                    </p>
                    <p className="text-xs" style={{ color: 'hsl(35 8% 32%)' }}>
                      All potential recipients must have an active lock to receive donations
                    </p>
                  </div>
                ) : (
                  <Button
                    onClick={handleDonate}
                    disabled={!user || amountNum <= 0 || totalRecipientCount === 0 || perPersonRaw <= 0}
                    className="w-full py-5 font-medium text-base"
                    style={{
                      background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                      color: 'hsl(30 8% 4%)',
                      border: 'none',
                      boxShadow: '0 0 24px hsl(38 65% 57% / 0.2)',
                      opacity: (!user || amountNum <= 0 || totalRecipientCount === 0 || perPersonRaw <= 0) ? 0.5 : 1,
                    }}
                  >
                    <Heart size={16} className="mr-2" />
                    {!user ? 'Connect Wallet to Donate' : `Donate to ${totalRecipientCount.toLocaleString()} Users`}
                  </Button>
                )}
                {allLockOwners.length === 0 && (
                  <p className="text-xs text-center" style={{ color: 'hsl(35 8% 40%)' }}>
                    {(allLocks ?? []).length === 0 ? 'Waiting for user pool to load…' : 'No other users to donate to yet.'}
                  </p>
                )}
              </div>
            </div>
          </BlurFade>

          {/* ─── Right: Selected Users ─────────────────────────────────────── */}
          <BlurFade delay={0.15}>
            <div className="rounded-xl overflow-hidden" style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}>
              <div className="flex items-center justify-between gap-2 px-5 py-4" style={{ borderBottom: '1px solid hsl(35 12% 14%)' }}>
                <div className="flex items-center gap-2">
                  <Star size={15} style={{ color: 'hsl(43 75% 68%)' }} />
                  <h2 className="font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem' }}>Your Selected Users</h2>
                </div>
                {unlockedSlots > 0 && favorites.length < unlockedSlots && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAddFavoriteOpen(true)}
                    className="text-xs"
                    style={{ color: 'hsl(38 65% 57%)', border: '1px solid hsl(38 65% 57% / 0.25)' }}
                  >
                    <Plus size={12} className="mr-1" />
                    Add
                  </Button>
                )}
              </div>

              <div className="p-5">
                {/* Points info */}
                <div
                  className="flex items-center justify-between px-4 py-2.5 rounded-lg text-sm"
                  style={{ background: 'hsl(30 5% 10%)', border: '1px solid hsl(35 12% 16%)' }}
                >
                  <span style={{ color: 'hsl(35 8% 52%)' }}>Your Selfless Points</span>
                  <span className="font-bold" style={{ color: 'hsl(43 75% 68%)', fontFamily: "'Cormorant Garamond', serif" }}>
                    {formatPoints(totalSelflessPoints)}
                  </span>
                </div>
                <div className="mb-4">
                  {pendingSelflessPoints > 0 && (
                    <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: 'hsl(35 8% 40%)' }}>
                      <Info size={10} />
                      {pendingSelflessPoints} {pendingSelflessPoints === 1 ? 'entry' : 'entries'} pending pricing — excluded from total above
                    </p>
                  )}
                </div>

                {/* Slot grid */}
                <div className="space-y-2 mb-4">
                  {unlockedSlots === 0 && nextSlotTier ? (
                    <div className="text-center py-6">
                      <Lock size={24} className="mx-auto mb-2" style={{ color: 'hsl(35 8% 35%)' }} />
                      <p className="text-sm font-medium mb-1" style={{ color: 'hsl(40 15% 75%)' }}>Selected Users Locked</p>
                      <p className="text-xs" style={{ color: 'hsl(35 8% 45%)' }}>
                        Earn {formatPoints(nextSlotTier.minPoints)} Selfless Points to unlock {nextSlotTier.slots} slots
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Filled slots */}
                      {favorites.map((fav) => {
                        const isEligible = activeLockOwners.has(fav.favoriteAddress);
                        return (
                          <div
                            key={fav.id}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-lg"
                            style={{
                              background: isEligible ? 'hsl(30 5% 10%)' : 'hsl(30 4% 8%)',
                              border: `1px solid ${isEligible ? 'hsl(35 12% 16%)' : 'hsl(35 10% 13%)'}`,
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center"
                                style={{
                                  background: isEligible ? 'hsl(43 75% 68% / 0.15)' : 'hsl(35 8% 12%)',
                                  border: `1px solid ${isEligible ? 'hsl(43 75% 68% / 0.3)' : 'hsl(35 8% 18%)'}`,
                                }}
                              >
                                <Star size={11} style={{ color: isEligible ? 'hsl(43 75% 68%)' : 'hsl(35 8% 38%)' }} />
                              </div>
                              <span className="font-mono text-sm" style={{ color: isEligible ? 'hsl(40 15% 75%)' : 'hsl(35 8% 45%)' }}>
                                {truncateAddress(fav.favoriteAddress)}
                              </span>
                              {!isEligible && (
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                                  style={{
                                    background: 'hsl(35 10% 12%)',
                                    color: 'hsl(35 8% 40%)',
                                    border: '1px solid hsl(35 10% 16%)',
                                    fontSize: '0.6rem',
                                  }}
                                >
                                  <Lock size={7} />
                                  No active lock
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveFavorite(fav)}
                              className="w-6 h-6 rounded flex items-center justify-center transition-colors"
                              style={{ color: 'hsl(35 8% 40%)' }}
                            >
                              <X size={13} />
                            </button>
                          </div>
                        );
                      })}

                      {/* Empty unlocked slots */}
                      {Array.from({ length: unlockedSlots - favorites.length }).map((_, i) => (
                        <button
                          key={`empty_${i}`}
                          onClick={() => setAddFavoriteOpen(true)}
                          className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-lg transition-colors"
                          style={{ background: 'hsl(30 5% 9%)', border: '1px dashed hsl(35 12% 16%)', color: 'hsl(35 8% 38%)' }}
                        >
                          <Plus size={13} />
                          <span className="text-sm">Add a user</span>
                        </button>
                      ))}

                      {/* Locked slots */}
                      {nextSlotTier && (
                        <div
                          className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg"
                          style={{ background: 'hsl(30 5% 8%)', border: '1px solid hsl(35 12% 12%)' }}
                        >
                          <Lock size={12} style={{ color: 'hsl(35 8% 30%)' }} />
                          <span className="text-xs" style={{ color: 'hsl(35 8% 35%)' }}>
                            +{nextSlotTier.slots - unlockedSlots} more slots at {formatPoints(nextSlotTier.minPoints)} pts
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Slot tier info */}
                <div
                  className="rounded-lg p-3 space-y-1.5"
                  style={{ background: 'hsl(30 5% 9%)', border: '1px solid hsl(35 12% 13%)' }}
                >
                  <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'hsl(35 8% 38%)' }}>Slot Unlocks</p>
                  {[
                    { pts: 100, slots: 2 }, { pts: 500, slots: 4 }, { pts: 1000, slots: 6 },
                    { pts: 2500, slots: 8 }, { pts: 5000, slots: 10 },
                  ].map((tier) => (
                    <div key={tier.pts} className="flex items-center justify-between text-xs">
                      <span style={{ color: totalSelflessPoints >= tier.pts ? 'hsl(38 65% 57%)' : 'hsl(35 8% 35%)' }}>
                        {formatPoints(tier.pts)} pts
                      </span>
                      <span style={{ color: totalSelflessPoints >= tier.pts ? 'hsl(43 75% 68%)' : 'hsl(35 8% 35%)' }}>
                        {tier.slots} slots {totalSelflessPoints >= tier.pts ? '✓' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </BlurFade>
        </div>

        {/* ─── Support the Creator ─────────────────────────────────────── */}
        <BlurFade delay={0.22}>
          <div className="max-w-5xl mx-auto mt-8">
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, hsl(330 60% 6%) 0%, hsl(30 6% 7%) 60%, hsl(270 30% 7%) 100%)',
                border: '1px solid hsl(330 50% 30% / 0.35)',
                boxShadow: '0 0 40px hsl(330 60% 50% / 0.06)',
              }}
            >
              {/* Subtle top glow */}
              <div
                className="absolute inset-x-0 top-0 h-px"
                style={{ background: 'linear-gradient(90deg, transparent, hsl(330 60% 55% / 0.5), transparent)' }}
              />

              <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10 p-6 md:p-8">
                {/* Icon + text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: 'hsl(330 60% 50% / 0.15)',
                        border: '1px solid hsl(330 60% 50% / 0.35)',
                      }}
                    >
                      <Coffee size={18} style={{ color: 'hsl(330 70% 65%)' }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3
                          className="font-semibold"
                          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem', color: 'hsl(330 70% 80%)' }}
                        >
                          Support the Creator
                        </h3>
                        <Sparkles size={13} style={{ color: 'hsl(330 60% 55%)' }} />
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'hsl(330 15% 55%)' }}>
                        Keep SelfWorth alive — and earn Selfless Points too
                      </p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'hsl(330 10% 55%)' }}>
                    If SelfWorth has brought you joy (or at least amusement), consider tipping the creator. Every donation earns you Selfless Points, and 100% goes directly to the creator's wallet.
                  </p>
                </div>

                {/* Donation controls */}
                <div className="flex flex-col gap-3 md:w-64 shrink-0">
                  {/* Token picker */}
                  <div className="flex gap-2">
                    {TOKENS.map((mint) => {
                      const t = getTokenByMint(mint);
                      const isSel = creatorToken === mint;
                      return (
                        <button
                          key={mint}
                          onClick={() => setCreatorToken(mint)}
                          className="flex-1 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all duration-200"
                          style={{
                            background: isSel ? t.brandColorBg : 'hsl(30 5% 10%)',
                            color: isSel ? t.brandColor : 'hsl(35 8% 40%)',
                            border: isSel ? `1px solid ${t.brandColorBorder}` : '1px solid hsl(35 12% 16%)',
                            boxShadow: isSel ? `0 0 10px ${t.brandColorBg}` : 'none',
                          }}
                        >
                          {t.symbol}
                        </button>
                      );
                    })}
                  </div>

                  {/* Amount input */}
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    placeholder={`Amount in ${getTokenByMint(creatorToken).symbol}`}
                    value={creatorAmount}
                    onChange={(e) => setCreatorAmount(e.target.value)}
                    style={{
                      background: 'hsl(330 5% 9%)',
                      border: '1px solid hsl(330 20% 20%)',
                      color: 'hsl(40 15% 88%)',
                    }}
                  />

                  {/* Donate button */}
                  <Button
                    onClick={handleCreatorDonate}
                    disabled={creatorDonating || !user || parseFloat(creatorAmount) <= 0}
                    className="w-full py-4 font-medium text-sm"
                    style={{
                      background: creatorDonating
                        ? 'hsl(330 20% 15%)'
                        : 'linear-gradient(135deg, hsl(330 55% 45%), hsl(330 65% 58%))',
                      color: creatorDonating ? 'hsl(330 20% 50%)' : 'hsl(0 0% 100%)',
                      border: 'none',
                      boxShadow: creatorDonating ? 'none' : '0 0 20px hsl(330 60% 50% / 0.25)',
                      opacity: (!user || parseFloat(creatorAmount) <= 0) && !creatorDonating ? 0.55 : 1,
                    }}
                  >
                    {creatorDonating ? (
                      <><Loader2 size={14} className="animate-spin mr-2" />Sending…</>
                    ) : (
                      <><Heart size={14} className="mr-2" />{user ? 'Donate to Creator' : 'Connect Wallet'}</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Success flash */}
              {creatorDoneOpen && (
                <div
                  className="flex items-center justify-between px-6 py-3"
                  style={{ borderTop: '1px solid hsl(330 50% 30% / 0.25)', background: 'hsl(330 40% 8%)' }}
                >
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'hsl(330 60% 70%)' }}>
                    <Heart size={14} />
                    <span>Thank you! Your generosity is deeply appreciated.</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold" style={{ color: 'hsl(43 75% 68%)', fontFamily: "'Cormorant Garamond', serif" }}>
                      +{formatPoints(creatorPointsEarned)} pts
                    </span>
                    <button
                      onClick={() => setCreatorDoneOpen(false)}
                      className="w-5 h-5 flex items-center justify-center rounded"
                      style={{ color: 'hsl(330 20% 50%)' }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </BlurFade>

        {/* Donation History */}
        {(myDonations ?? []).length > 0 && (
          <div className="max-w-5xl mx-auto mt-8">
            <DonationHistory donations={myDonations ?? []} walletAddress={walletAddress ?? ''} />
          </div>
        )}

        {/* Back link */}
        <BlurFade delay={0.3}>
          <div className="max-w-5xl mx-auto mt-10 text-center">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm" style={{ color: 'hsl(35 8% 45%)' }}>
              <ChevronRight size={14} className="rotate-180" />
              Back to Dashboard
            </Link>
          </div>
        </BlurFade>
      </div>

      {/* Modals */}
      <AddFavoriteModal
        open={addFavoriteOpen}
        onClose={() => setAddFavoriteOpen(false)}
        candidates={allLockOwners}
        existingFavorites={favoriteAddresses}
        activeLockOwners={activeLockOwners}
        onAdd={handleAddFavorite}
      />

      <DonationProgressModal
        open={progressOpen}
        current={progressCurrent}
        total={progressTotal}
        failed={progressFailed}
        skipped={progressSkipped}
        done={progressDone}
        totalDonated={progressTotalDonated}
        pointsEarned={progressPoints}
        onClose={() => setProgressOpen(false)}
      />
    </PageLayout>
  );
};

export default DonatePage;
